import { useState, useMemo, useEffect } from 'react';
import { Modal, Form, Button, Alert, Row, Col } from 'react-bootstrap';
import { redisFetch } from '../../features/jobs/api/http';

const PHASE_LABEL = {
  unwinding: 'Unwinding',
  pre_stranding: 'Pre-Stranding',
  conductor_isolation: 'Isolation',
  rope: 'Rope',
  interior_sheat: 'Interior Sheath',
  armouring: 'Armouring',
  exterior_sheat: 'Exterior Sheath',
  coiling: 'Coiling',
  rewinding: 'Rewinding'
};
const phaseLabel = p => PHASE_LABEL[p] || p || '-';

// The target's merge group = the target job + every queue job that SHARES a token
// with it (merge-execution). Group-by jobs keep individual tokens, so they don't
// share and the group is just [target].
function mergeGroupOf(targetJobId, allJobs) {
  const target = (allJobs || []).find(j => String(j.jobId) === String(targetJobId));
  if (!target) return targetJobId ? [String(targetJobId)] : [];
  const tgtTokens = new Set(
    (target.queue || [])
      .flatMap(t => (t.outputs || []).map(o => o?.tokenId))
      .filter(Boolean)
  );
  const group = new Set([String(targetJobId)]);
  for (const j of allJobs || []) {
    if (String(j.jobId) === String(targetJobId)) continue;
    const shares = (j.queue || []).some(t =>
      (t.outputs || []).some(o => tgtTokens.has(o?.tokenId))
    );
    if (shares) group.add(String(j.jobId));
  }
  return [...group];
}

// Move LENGTH (full or partial) from one or more source drums to a target product.
// The queue order is preserved; the source drum stays in place with its length
// reduced (0 on a full move); the moved length becomes a NEW drum at the target
// (shared across the target's merge group if it is a merge execution).
const MoveToProductModal = ({ show, onHide, block, allJobs = [], machineId, onDone }) => {
  const items = useMemo(
    () =>
      (block?.items || []).map(it => {
        const t = it.task || {};
        const g = it.group || {};
        const tokenId = (t.outputs || [])[0]?.tokenId || null;
        return {
          key: `${g.jobId}::${t.taskId}::${tokenId}`,
          sourceJobId: g.jobId,
          sourceProduct: g.productLabel || g.name || g.jobId,
          taskId: t.taskId,
          tokenId,
          phase: t.phase,
          color: t.color,
          plannedLength: Number(t.plannedLength || 0)
        };
      }),
    [block]
  );

  const [rows, setRows] = useState([]);
  const [targetJobId, setTargetJobId] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (show) {
      setRows(items.map(i => ({ key: i.key, include: true, amount: i.plannedLength })));
      setTargetJobId('');
      setPreview(null);
      setErr(null);
    }
  }, [show, items]);

  const targetJobIds = useMemo(
    () => (targetJobId ? mergeGroupOf(targetJobId, allJobs) : []),
    [targetJobId, allJobs]
  );
  const isMergeTarget = targetJobIds.length > 1;
  const targetProducts = targetJobIds.map(
    id => (allJobs || []).find(j => String(j.jobId) === String(id))?.productLabel || id
  );

  const setRow = (key, patch) =>
    setRows(rs => rs.map(r => (r.key === key ? { ...r, ...patch } : r)));

  const selected = rows
    .filter(r => r.include && Number(r.amount) > 0)
    .map(r => ({ ...r, meta: items.find(i => i.key === r.key) }));

  const run = async dryRun => {
    setErr(null);
    setBusy(true);
    try {
      const results = [];
      for (const s of selected) {
        const res = await redisFetch(
          `/api/redis/queues/${machineId}/move-length`,
          {
            method: 'POST',
            body: JSON.stringify({
              source: {
                jobId: s.meta.sourceJobId,
                taskId: s.meta.taskId,
                tokenId: s.meta.tokenId
              },
              targetJobIds,
              amount: Number(s.amount),
              dryRun
            })
          }
        );
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
        results.push(data);
      }
      if (dryRun) setPreview(results);
      else onDone?.();
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setBusy(false);
    }
  };

  if (!block) return null;
  const targetOptions = (allJobs || []).filter(j => j.jobId);

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title className="fs-7">Move length to another product</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="fs-9 fw-semibold text-700 mb-2">
          Source drum(s) — set how much to move (full length by default):
        </div>
        {items.map(i => {
          const r = rows.find(x => x.key === i.key) || {};
          return (
            <Row key={i.key} className="align-items-center g-2 mb-2">
              <Col xs="auto">
                <Form.Check
                  type="checkbox"
                  checked={!!r.include}
                  disabled={items.length === 1}
                  onChange={e => {
                    setRow(i.key, { include: e.target.checked });
                    setPreview(null);
                  }}
                />
              </Col>
              <Col>
                <span className="fs-9">
                  <b>{i.sourceProduct}</b> · {phaseLabel(i.phase)} · {i.color || '-'}{' '}
                  · <span className="font-monospace text-700">{i.tokenId || '-'}</span>
                </span>
              </Col>
              <Col xs="auto" className="d-flex align-items-center gap-2">
                <Form.Control
                  size="sm"
                  type="number"
                  min={0}
                  max={i.plannedLength}
                  style={{ width: 100 }}
                  value={r.amount ?? ''}
                  disabled={!r.include}
                  onChange={e => {
                    setRow(i.key, { amount: e.target.value });
                    setPreview(null);
                  }}
                />
                <span className="fs-10 text-700">m of {i.plannedLength}</span>
              </Col>
            </Row>
          );
        })}

        <Form.Group className="mb-2 mt-3">
          <Form.Label className="fs-9 fw-semibold text-700">To product:</Form.Label>
          <Form.Select
            size="sm"
            value={targetJobId}
            onChange={e => {
              setTargetJobId(e.target.value);
              setPreview(null);
            }}
          >
            <option value="">— choose target product —</option>
            {targetOptions.map(j => (
              <option key={j.jobId} value={j.jobId}>
                {j.productLabel || j.name || j.jobId} ({String(j.jobId).slice(0, 6)})
              </option>
            ))}
          </Form.Select>
          {isMergeTarget && (
            <div className="text-info fs-10 mt-1">
              Merge target → creates a shared drum in:{' '}
              <b>{targetProducts.join(', ')}</b>
            </div>
          )}
        </Form.Group>

        {err && (
          <Alert variant="danger" className="fs-9 mb-2">
            {err}
          </Alert>
        )}

        {preview && (
          <Alert variant="info" className="fs-9 mb-0">
            <div className="fw-semibold mb-1">Preview (nothing written yet):</div>
            {preview.map((p, i) => (
              <div key={i}>
                → move <b>{p.source?.movedAmount}m</b> from{' '}
                <b>{p.source?.product}</b> (leaves {p.source?.remaining}m) · new drum
                {p.shared ? ' (shared)' : ''} at{' '}
                <b>{(p.created || []).map(c => c.product).join(' + ')}</b>
              </div>
            ))}
            <div className="text-700 fs-10 mt-1">
              Queue order preserved; the source drum stays in place; new drums are
              appended.
            </div>
          </Alert>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="link" className="text-600" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="outline-primary"
          disabled={busy || !targetJobId || !selected.length}
          onClick={() => run(true)}
        >
          {busy ? '...' : 'Preview'}
        </Button>
        <Button
          variant="primary"
          disabled={busy || !targetJobId || !selected.length || !preview}
          onClick={() => run(false)}
        >
          {busy ? 'Applying...' : 'Move'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default MoveToProductModal;
